import {
  CREATE_PACKAGE_DEPLOYMENT,
  DELETE_PACKAGE_DEPLOYMENT,
  FETCH_PACKAGE_DEPLOYMENT_BY_ID,
  FETCH_PACKAGE_DEPLOYMENT_SUBSCRIPTIONS,
  FETCH_PACKAGE_DEPLOYMENTS,
  SEARCH_PACKAGE_DEPLOYMENTS,
  UPDATE_PACKAGE_DEPLOYMENT,
} from "@apiUtils";
import {
  mutationFetch,
  type SearchPayload,
  type UseSearchQueryOptions,
  useSearchQuery,
} from "@atlas/sdk";
import type { IHubCatalog, IHubPackageDeployment } from "@models";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";

export const HUB_PACKAGE_DEPLOYMENTS_KEY = "hubPackageDeploymentsKey";
export const HUB_PACKAGE_DEPLOYMENT_BY_ID_KEY = "hubPackageDeploymentByIdKey";
export const HUB_PACKAGE_DEPLOYMENT_SUBSCRIPTIONS_KEY =
  "hubPackageDeploymentSubscriptionsKey";

export const getHubPackageDeploymentsKey = (companyId: number) =>
  [HUB_PACKAGE_DEPLOYMENTS_KEY, companyId] as const;

export const getHubPackageDeploymentByIdKey = (deploymentId: number) =>
  [HUB_PACKAGE_DEPLOYMENT_BY_ID_KEY, deploymentId] as const;

export const getHubPackageDeploymentSubscriptionsKey = (userId: string) =>
  [HUB_PACKAGE_DEPLOYMENT_SUBSCRIPTIONS_KEY, userId] as const;

export const useHubPackageDeployments = (
  companyId: number,
  options?: Partial<
    UseQueryOptions<
      IHubPackageDeployment[],
      AxiosError,
      IHubPackageDeployment[],
      ReturnType<typeof getHubPackageDeploymentsKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubPackageDeploymentsKey(companyId),
    queryFn: async () => {
      return mutationFetch<IHubPackageDeployment[]>({
        url: FETCH_PACKAGE_DEPLOYMENTS,
        method: "GET",
        headers: {
          companyId,
        },
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && companyId > 0,
  });
};

export const useHubPackageDeployment = (
  deploymentId: number,
  options?: Partial<
    UseQueryOptions<
      IHubPackageDeployment,
      AxiosError,
      IHubPackageDeployment,
      ReturnType<typeof getHubPackageDeploymentByIdKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubPackageDeploymentByIdKey(deploymentId),
    queryFn: async () => {
      return mutationFetch<IHubPackageDeployment>({
        url: FETCH_PACKAGE_DEPLOYMENT_BY_ID.replace(
          ":deploymentId",
          String(deploymentId),
        ),
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && deploymentId > 0,
  });
};

export const useHubPackageDeploymentSubscriptions = (
  userId: string,
  options?: Partial<
    UseQueryOptions<
      IHubCatalog[],
      AxiosError,
      IHubCatalog[],
      ReturnType<typeof getHubPackageDeploymentSubscriptionsKey>
    >
  >,
) => {
  const trimmedUserId = userId.trim();
  return useQuery({
    queryKey: getHubPackageDeploymentSubscriptionsKey(trimmedUserId),
    queryFn: async () => {
      return mutationFetch<IHubCatalog[]>({
        url: FETCH_PACKAGE_DEPLOYMENT_SUBSCRIPTIONS,
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && trimmedUserId.length > 0,
  });
};

export type CreateHubPackageDeploymentInput = {
  body: IHubPackageDeployment;
  companyId: number;
};

const createHubPackageDeployment = ({
  body,
  companyId,
}: CreateHubPackageDeploymentInput): Promise<IHubPackageDeployment> => {
  if (companyId <= 0) {
    throw new Error("Create package deployment requires a valid companyId.");
  }
  return mutationFetch<IHubPackageDeployment>({
    url: CREATE_PACKAGE_DEPLOYMENT,
    method: "POST",
    body,
    headers: {
      companyId,
    },
  });
};

const updateHubPackageDeployment = (
  deploymentId: number,
  body: IHubPackageDeployment,
): Promise<IHubPackageDeployment> => {
  return mutationFetch<IHubPackageDeployment>({
    url: UPDATE_PACKAGE_DEPLOYMENT.replace(
      ":deploymentId",
      String(deploymentId),
    ),
    method: "PUT",
    body,
  });
};

const deleteHubPackageDeployment = (deploymentId: number): Promise<void> => {
  return mutationFetch<void>({
    url: DELETE_PACKAGE_DEPLOYMENT.replace(
      ":deploymentId",
      String(deploymentId),
    ),
    method: "DELETE",
  });
};

export const useCreateHubPackageDeployment = (
  options?: UseMutationOptions<
    IHubPackageDeployment,
    AxiosError,
    CreateHubPackageDeploymentInput,
    unknown
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createHubPackageDeployment,
    ...options,
    onSuccess: (
      data: IHubPackageDeployment,
      variables: CreateHubPackageDeploymentInput,
      context: unknown,
      mutation,
    ) => {
      queryClient.invalidateQueries({
        queryKey: getHubPackageDeploymentsKey(variables.companyId),
      });
      queryClient.invalidateQueries({
        queryKey: [SEARCH_PACKAGE_DEPLOYMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [HUB_PACKAGE_DEPLOYMENT_SUBSCRIPTIONS_KEY],
      });
      if (data.deploymentId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubPackageDeploymentByIdKey(data.deploymentId),
        });
      }
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, mutation);
      }
    },
  });
};

export const useUpdateHubPackageDeployment = (
  options?: UseMutationOptions<
    IHubPackageDeployment,
    AxiosError,
    IHubPackageDeployment,
    unknown
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: IHubPackageDeployment) => {
      const deploymentId = body.deploymentId;
      if (deploymentId == null) {
        throw new Error("deploymentId is required for update");
      }
      return updateHubPackageDeployment(deploymentId, body);
    },
    ...options,
    onSuccess: (
      data: IHubPackageDeployment,
      variables: IHubPackageDeployment,
      context: unknown,
      mutation,
    ) => {
      queryClient.invalidateQueries({
        queryKey: [HUB_PACKAGE_DEPLOYMENTS_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [SEARCH_PACKAGE_DEPLOYMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [HUB_PACKAGE_DEPLOYMENT_SUBSCRIPTIONS_KEY],
      });
      if (data.deploymentId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubPackageDeploymentByIdKey(data.deploymentId),
        });
      }
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, mutation);
      }
    },
  });
};

export const useDeleteHubPackageDeployment = (
  options?: UseMutationOptions<void, AxiosError, number, unknown>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteHubPackageDeployment,
    ...options,
    onSuccess: (_data, deploymentId: number, context: unknown, mutation) => {
      queryClient.invalidateQueries({
        queryKey: [HUB_PACKAGE_DEPLOYMENTS_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [SEARCH_PACKAGE_DEPLOYMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [HUB_PACKAGE_DEPLOYMENT_SUBSCRIPTIONS_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: getHubPackageDeploymentByIdKey(deploymentId),
      });
      if (options?.onSuccess) {
        options.onSuccess(_data, deploymentId, context, mutation);
      }
    },
  });
};

export const useSearchHubPackageDeployments = (
  payload: SearchPayload,
  options?: Omit<
    UseSearchQueryOptions<IHubPackageDeployment>,
    "url" | "payload"
  >,
) => {
  return useSearchQuery<IHubPackageDeployment>({
    url: SEARCH_PACKAGE_DEPLOYMENTS,
    payload,
    ...options,
  });
};
